import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../components/hooks";
import { markOnboarded } from "../../store/slices/onboardingSlice";
import { useStyles } from "./Onboarding.style";
import clsx from "classnames";

import firstSlide1 from "./assets/first_slide@1x.webp";
import firstSlide2 from "./assets/first_slide@2x.webp";
import firstSlide3 from "./assets/first_slide@3x.webp";

import secondSlide1 from "./assets/second_slide@1x.webp";
import secondSlide2 from "./assets/second_slide@2x.webp";
import secondSlide3 from "./assets/second_slide@3x.webp";

import thirdSlide1 from "./assets/third_slide@1x.webp";
import thirdSlide2 from "./assets/third_slide@2x.webp";
import thirdSlide3 from "./assets/third_slide@3x.webp";
import arrowRight from "./assets/arrow-right.svg";

import { StaticImage } from "../../components/common/StaticImage";
import { Typography } from "../../components/core/Typography/Typography";
import { TypographyVariant } from "../../constants/typography";
import Button from "../../components/core/Button/Button";
import { SliderContainer } from "../../components/common/SliderContainer";
import { useNavigate } from "react-router-dom";
import { PageRoutes } from "../../constants/historyGaming";
import { t } from "i18next";
import { assetsImages } from "../../models/theme";
import { login } from "@onmo/auth";
import { FEATURES } from "../../types/theme";

const slides = [
  {
    src: firstSlide1,
    src2x: firstSlide2,
    src3x: firstSlide3,
    title: "Play Everything Here",
    description:
      "Discover every game you love in one portal, with instant access to endless fun, challenges, and exclusive content.",
  },
  {
    src: secondSlide1,
    src2x: secondSlide2,
    src3x: secondSlide3,
    title: "Climb the Ranks",
    description:
      "Compete against friends and rivals, track your progress, and rise to the top of the leaderboard.",
  },
  {
    src: thirdSlide1,
    src2x: thirdSlide2,
    src3x: thirdSlide3,
    title: "Earn & Redeem",
    description:
      "Earn Vodabucks by playing games, then redeem them for data packs, vouchers, and exclusive real-world items.",
  },
];

const Onboarding: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const { theme } = useAppSelector((state) => state.theme);
  const hideGuestPage = theme?.hideFeature?.includes(FEATURES.guestPage) || false;

  const handleFinish = async () => {
    dispatch(markOnboarded());
    if (!hideGuestPage) {
      navigate(PageRoutes.Guest);
      return;
    }
    await login(window.location.origin);
  };

  const handleNext = async () => {
    if (currentIndex < slides.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      await handleFinish();
    }
  };

  const { title, description } = slides[currentIndex];
  const buttontext = currentIndex < slides.length - 1 ? t("next") : t("common_Close");

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <img
          src={assetsImages.headerLogo}
          alt="Vodacom Logo"
          className={classes.logo}
        />
        <Button
          variant="borderless"
          color="primary"
          onClick={handleFinish}
          className={classes.skipTxt}
        >
          {t("skip")}
        </Button>
      </div>

      <div className={classes.space32} />

      {/* Slider */}
      <div className={classes.slider}>
        <SliderContainer
          nbSlide={slides.length}
          dotsClass={classes.paginationDots}
          activeSlide={currentIndex}
          settings={{
            autoplay: false,
            infinite: false,
            arrows: false,
            variableWidth: false,
            adaptiveHeight: true,
            responsive: [
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                },
              },
              {
                breakpoint: 600,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                },
              },
            ],
          }}
          afterChangeSlider={(next) => setCurrentIndex(next)}
        >
          {slides.map((slide, idx) => (
            <div className={classes.slide} key={idx}>
              <div className={classes.imageCtr}>
                <StaticImage
                  src={slide.src}
                  src2x={slide.src2x}
                  src3x={slide.src3x}
                  className={classes.image}
                  alt={slide.title}
                />
              </div>
            </div>
          ))}
        </SliderContainer>
      </div>

      {/* Dots */}
      <div className={classes.paginationDots}>
        {slides.map((_, idx) => (
          <span key={idx} className={clsx({ active: idx === currentIndex })} />
        ))}
      </div>

      {/* Text */}
      <div className={classes.textWrapper}>
        <Typography variant={TypographyVariant.HeadingH4Bold}>
          {title}
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB5Regular}
          className={classes.marginDes}
        >
          {description}
        </Typography>
      </div>

      {/* Button */}
      <div className={classes.buttonWrapper}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleNext}
        >
          {buttontext}
          <img src={arrowRight} alt="Next" className={classes.buttonImg} />
        </Button>
      </div>
    </div>
  );
};

export default Onboarding;
